import { MouseEvent, ReactNode, useCallback } from 'react';

export interface BreadcrumbProps {
  /** The striing or ReactNode in between each of the items */
  separator?: string | ReactNode;
  /** An array of links. Each breadcrumb can have an optional icon and title */
  items: BreadcrumbItem[];
  /** The callback for clicking a link */
  onBreadcrumbClick: (href: string | undefined) => void;
}

export interface BreadcrumbItem {
  link: string;
  title?: string;
  current?: boolean;
  icon?: React.FunctionComponent<
    React.SVGProps<SVGSVGElement> & {
      title?: string | undefined;
    }
  >;
}

export type BreadcrumbLinkType = 'react-router' | 'html';

const navClasses = 'flex';
const breadcrumbListWrapperClasses = 'inline-flex items-center space-x-1';
const breadcrumbItemWrapperClasses = 'inline-flex items-center space-x-1';
const separatorWrapperClasses = 'text-gray-700 font-normal';
const currentItemClasses = 'text-gray-900 font-bold';
const linkClasses = 'hover:underline flex';
const iconClasses = 'h-5 w-5';

const BreadcrumbText = (props: { item: BreadcrumbItem; onBreadcrumbClick: (href: string | undefined) => void }) => {
  const { item, onBreadcrumbClick } = props;

  const clickHandler = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      onBreadcrumbClick(event.currentTarget.dataset.link);
    },
    [onBreadcrumbClick]
  );

  const iconElement = item.icon && item.icon({ className: iconClasses });

  // Current item is not meant to be a clickable link
  return item.current ? (
    <span role="link" aria-current="page" className={currentItemClasses}>
      {iconElement} {item.title}
    </span>
  ) : (
    <a className={linkClasses} href={item.link} data-link={item.link} onClick={clickHandler}>
      {iconElement} {item.title}
    </a>
  );
};

const BreadcrumbListItem = (props: {
  item: BreadcrumbItem;
  isLastItem: boolean;
  separator: ReactNode;
  onBreadcrumbClick: (href: string | undefined) => void;
}) => {
  const { item, isLastItem, separator, onBreadcrumbClick } = props;

  return (
    <div className={breadcrumbItemWrapperClasses}>
      <BreadcrumbText item={item} onBreadcrumbClick={onBreadcrumbClick} />
      {!isLastItem && <span className={separatorWrapperClasses}>{separator}</span>}
    </div>
  );
};

export function Breadcrumb({ separator = '/', ...props }: BreadcrumbProps) {
  const { items, onBreadcrumbClick } = props;

  return (
    <nav className={navClasses} aria-label="Breadcrumb">
      <div className={breadcrumbListWrapperClasses}>
        {items.map((item: BreadcrumbItem, index) => (
          <BreadcrumbListItem
            key={item.link}
            item={item}
            isLastItem={index === items.length - 1}
            separator={separator}
            onBreadcrumbClick={onBreadcrumbClick}
          />
        ))}
      </div>
    </nav>
  );
}

export default Breadcrumb;
